import { useEffect, type ReactNode } from 'react'
import { api, refreshAccessToken } from '@/shared/api/client'
import { useSession } from './session'
import { config } from '@/config/runtime-config'
import type { EffectiveAuthorities, UserResponse } from '@/shared/api/types'

/**
 * Populate the session.
 *
 * TWO calls, deliberately:
 *   GET /users/me            → UserResponse (the profile)
 *   GET /rbac/me/authorities → EffectiveAuthorities (what they may do)
 *
 * `/users/me` returns a FLAT UserResponse — it does not carry authorities. An
 * earlier version of this file read `me.authorities` off it, got `undefined`,
 * and every permission check therefore failed. The visible symptom was a
 * super-user seeing an empty navigation: the wildcard grant was fine, the
 * client was simply never asking for it.
 */
export async function hydrateSession(token: string) {
  const [user, eff] = await Promise.all([
    api.get<UserResponse>('/users/me'),
    api.get<EffectiveAuthorities>('/rbac/me/authorities').catch(
      // A user with no roles still has a valid session — they just cannot do
      // anything. That must render as an empty console, not a failed login.
      () => ({ userId: '', authorities: [], fingerprint: '' }) as EffectiveAuthorities,
    ),
  ])
  useSession.getState().setAuthenticated(token, user, eff.authorities ?? [], eff.fingerprint)
  return { user, authorities: eff.authorities ?? [] }
}

/**
 * The boot sequence.
 *
 *   mount → POST /auth/refresh (HttpOnly cookie, no body)
 *             ├─ 200 → token in memory → hydrate → render
 *             └─ 401 → anonymous → login route
 *
 * A page reload restores the session through the cookie. That is precisely why
 * nothing is persisted in web storage.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const status = useSession((s) => s.status)
  const setAnonymous = useSession((s) => s.setAnonymous)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const token = await refreshAccessToken()
      if (cancelled) return
      if (!token) { setAnonymous(); return }
      try {
        await hydrateSession(token)
      } catch {
        if (!cancelled) setAnonymous()
      }
    })()
    return () => { cancelled = true }
  }, [setAnonymous])

  if (status === 'unknown') {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3">
        <span className="brand-mark" style={{ width: 28, height: 28, fontSize: 14 }}>
          {config.brandMark}
        </span>
        <div className="text-[length:var(--text-sm)] text-[color:var(--color-ink-3)]">
          Restoring session…
        </div>
      </div>
    )
  }
  return <>{children}</>
}
